import { AdvancedThresholdElement } from "@/overlay/components/CommonParts/Threshold/AdvancedThreshold/AdvancedThresholdElement";
import { AdvancedThresholdHeader } from "@/overlay/components/CommonParts/Threshold/AdvancedThreshold/AdvancedThresholdHeader";
import {
  type AdvancedThreshold,
  type AdvancedThresholdDetails,
  type BasicCondition,
  getDefaultAdvancedThresholdDetails,
} from "@/overlay/components/CommonParts/Threshold/types";
import { t } from "@lingui/core/macro";
import { MmDropdown, MmTooltipWrapper } from "@mm/zeus-react";
import React from "react";
import styles from "./AdvancedThreshold.module.scss";

export type AdvancedThresholdDetailsProps = {
  threshold: AdvancedThreshold;
  onChange: (updatedThreshold: Partial<AdvancedThreshold>) => void;
  onDelete: () => void;
  isPoi?: boolean;
};
const BASIC_CONDITIONS: BasicCondition[] = ["and", "or"];
export const AdvancedThresholdRow = (props: AdvancedThresholdDetailsProps) => {
  const { threshold, onChange, onDelete, isPoi } = props;

  const handleDetailChange = (updatedDetail: Partial<AdvancedThresholdDetails>, index: number) => {
    const details = [...threshold.details];
    details[index] = { ...details[index], ...updatedDetail };
    onChange({ id: threshold.id, details: details });
  };

  const addThirdOption = () => {
    const details = [...threshold.details, getDefaultAdvancedThresholdDetails()];
    const conditions = [...threshold.conditions];
    conditions.push("and");

    onChange({ id: threshold.id, conditions: conditions, details: details });
  };

  const removeThirdOption = () => {
    const details = threshold.details.slice(0, -1);
    const conditions = threshold.conditions.slice(0, -1);
    onChange({ id: threshold.id, conditions, details });
  };
  return (
    <div className={styles.advancedThresholdContainer}>
      <AdvancedThresholdHeader
        color={threshold.color}
        label={threshold.label}
        onColorChange={(newColor) => onChange({ id: threshold.id, color: newColor })}
        onLabelChange={(newTitle) => onChange({ id: threshold.id, label: newTitle })}
        onDelete={onDelete}
      />
      <div
        className={
          threshold?.conditions?.length <= 1 ? styles.advancedThresholdTwoRows : styles.advancedThresholdThreeRows
        }
      >
        <AdvancedThresholdElement
          advancedProps={threshold.details[0]}
          onPropsChange={(newDetails) => handleDetailChange(newDetails, 0)}
          isPoi={isPoi}
        />
        <MmDropdown
          items={BASIC_CONDITIONS.map((f) => ({ id: f, title: `${f.toUpperCase()}` }))}
          verticalAlignment="auto"
          activeId={threshold.conditions?.[0] ? [threshold.conditions?.[0]] : []}
          itemSelectedHandler={(selection) => {
            const conditions = [selection[0].id as BasicCondition];
            if (threshold?.conditions[1]) {
              conditions.push(threshold.conditions[1]);
            }
            onChange({ id: threshold.id, conditions: conditions });
          }}
        />

        <AdvancedThresholdElement
          advancedProps={threshold.details[1]}
          onPropsChange={(newDetails) => handleDetailChange(newDetails, 1)}
          isPoi={isPoi}
        />
        {threshold?.conditions?.length <= 1 ? (
          <>
            <MmTooltipWrapper
              className={styles.addButtonContainer}
              tail={true}
              position="top-left"
              tooltip-text={t`Add Option`}
            >
              <button type={"button"} onClick={() => addThirdOption()}>
                <span className={"material-icons"}>add_circle</span>
              </button>
            </MmTooltipWrapper>
          </>
        ) : (
          <>
            <MmDropdown
              items={BASIC_CONDITIONS.map((f) => ({ id: f, title: `${f.toUpperCase()}` }))}
              verticalAlignment="auto"
              activeId={threshold.conditions?.[1] ? [threshold.conditions?.[1]] : []}
              itemSelectedHandler={(selection) =>
                onChange({ id: threshold.id, conditions: [threshold.conditions[0], selection[0].id as BasicCondition] })
              }
            />

            <AdvancedThresholdElement
              advancedProps={threshold.details[2]}
              onPropsChange={(newDetails) => handleDetailChange(newDetails, 2)}
              onRemoveOption={() => removeThirdOption()}
              isPoi={isPoi}
            />
          </>
        )}
      </div>
    </div>
  );
};
